import { exportCsv } from "json2csv-export";
import * as XLSX from "xlsx-js-style";
import { Batch, Cart, FulfilmentList, Payment, SKUItem, Student } from "../interfaces";
import moment from "moment";
import { get } from "lodash";
import { formatDateTimeIst } from "./mixin";
const NOT_AVAILABLE = "?";

const EXCEL_HEADER_STYLE = {
  font: { bold: true, sz: 11, color: { rgb: "1A1A1A" } },
  fill: { patternType: "solid" as const, fgColor: { rgb: "FFFF00" } },
  alignment: { horizontal: "center" as const, vertical: "center" as const, wrapText: true },
  border: {
    top: { style: "thin" as const, color: { rgb: "C4C400" } },
    bottom: { style: "thin" as const, color: { rgb: "C4C400" } },
    left: { style: "thin" as const, color: { rgb: "C4C400" } },
    right: { style: "thin" as const, color: { rgb: "C4C400" } },
  },
};

function styleExcelHeaderRow(ws: XLSX.WorkSheet, columnCount: number) {
  for (let c = 0; c < columnCount; c += 1) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    const cell = ws[addr];
    if (!cell) continue;
    cell.s = EXCEL_HEADER_STYLE;
  }
  ws["!rows"] = [{ hpt: 22 }];
}

function autoFitExcelColumns(
  ws: XLSX.WorkSheet,
  rows: Record<string, unknown>[],
  headers: string[],
) {
  ws["!cols"] = headers.map((h) => {
    const longest = Math.max(
      h.length,
      ...rows.map((r) => String(r[h] ?? "").length),
    );
    return { wch: Math.min(Math.max(longest + 2, 14), 40) };
  });
}

const blank = (value?: string | null) =>
  value == null || String(value).trim() === "" ? "" : String(value).trim();

const flattenText = (value?: string | null) =>
  value ? value.replace(/[\r\n]+/g, ", ").trim() : "";

function formatIstDateTime(value?: string | Date | null) {
  if (!value) return "";
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.utcOffset(330).format("DD MMM YYYY hh:mm A") : "";
}

function verificationStatusLabel(student: Student) {
  if (student.verified) return "Approved";
  if (student.verification_requested) return "Pending";
  if (student.verification_rejected) return "Rejected";
  return "Not requested";
}

/**
 * Shared student columns for Excel exports.
 * `status` is batch enrollment for batch sheets, account active/inactive otherwise.
 */
const studentExportColumns = (
  student: Student,
  extras: { Status: string; "Enrolled On"?: string },
) => ({
  Name: student.name,
  Email: student.email,
  "Registration Number": student.registration_number,
  Mobile: student.mobile_number,
  "Alternate Number": blank(student.alternate_number),
  Birthday: student.dob,
  Category: student.category?.name || "?",
  Language: student.language?.name || "",
  Classification: student.classification?.name || "",
  Gender: student.gender,
  "Fathers Name": student.fathers_name,
  Tags: (student.tags ?? []).map((t) => t.name).filter(Boolean).join(", "),
  "Banned Status": student.active ? "No" : "Yes",
  Status: extras.Status,
  "Profile Verified": student.verified ? "Yes" : "No",
  "Verification Status": verificationStatusLabel(student),
  "Email Verified": student.is_email_verified ? "Yes" : "No",
  Notes: flattenText(student.admin_note) || "?",
  Employment: student.employment,
  "Study Center": student.study_center,
  qualification: student.qualification,
  "Place of Supply": blank(student.state),
  "Billing Address": flattenText(student.billing_address),
  "Last Login": formatIstDateTime(student.last_login),
  "Registered On": formatIstDateTime(student.created_at),
  ...(extras["Enrolled On"] !== undefined ? { "Enrolled On": extras["Enrolled On"] } : {}),
  ...defaultAddressExportColumns(student),
});

/**
 * Default address book columns for student exports.
 * Missing values stay blank (not "?") so sheets stay clean until students add addresses.
 */
const defaultAddressExportColumns = (student: Student) => {
  const a = student.default_address;
  return {
    "Address Label": blank(a?.label),
    "Address Line 1": blank(a?.address_line_1),
    "Address Line 2": blank(a?.address_line_2),
    Landmark: blank(a?.landmark),
    City: blank(a?.city),
    "Post Office": blank(a?.post_office),
    District: blank(a?.district),
    State: blank(a?.state),
    Country: blank(a?.country),
    "Country Code": blank(a?.country_code),
    Pincode: blank(a?.postal_code),
    "Delivery Instructions": blank(a?.delivery_instructions),
  };
};

export const exportCartReport = (carts: Cart[]) => {
  return exportCsv({
    header: {
      reference_no: "Reference No",
      student_name: "Student Name",
      registration_number: "Registration Number",
      email: "Email",
      payment_status: "Payment Status",
      payment_method: "Payment Method",
      cart_total_amount: "Cart Total Amount",
      item_name: "Item Name",
      item_price: "Item Price",
      item_quantity: "Item Quantity",
      item_total: "Item Total",
      payment_date: "Payment Date",
      created_at: "Created At",
      description: "Description",
    },
    data: carts.flatMap((cart) => {
      const baseRow = {
        reference_no: cart.reference_no,
        student_name: cart.student?.name || "-",
        registration_number: cart.student?.registration_number || "-",
        email: cart.student?.email || "-",
        payment_status: cart.payment_status || "-",
        payment_method: cart.payment_method || "-",
        cart_total_amount: cart.total_amount ?? 0,
        payment_date: cart.payment_date
          ? moment.utc(cart.payment_date).utcOffset(330).format("DD MMM YYYY hh:mm A")
          : "-",
        created_at: cart.created_at
          ? moment.utc(cart.created_at).utcOffset(330).format("DD MMM YYYY hh:mm A")
          : "-",
        description: cart.description || "-",
      };

      return cart.items && cart.items.length
        ? cart.items.map((item) => ({
            ...baseRow,
            item_name: item.item_name,
            item_price:
              item.price ?? Number(item.price_snapshot),
            item_quantity: item.quantity,
            item_total:
              item.total ??
              Number(item.price_snapshot) * (item.quantity || 1),
          }))
        : [
            {
              ...baseRow,
              item_name: "-",
              item_price: 0,
              item_quantity: 0,
              item_total: 0,
            },
          ];
    }),
    filename: `cart-report-${new Date().toLocaleDateString()}-${new Date().getTime()}`,
  });
};

export const exportBatches = (batches: Batch[]) => {
  return exportCsv({
    header: {
      name: "Name",
      course_code: "Course Code",
      category: "Category",
      enrolled_students: "Enrollment",
      active_students: "Active",
    },
    data: batches.map((e: Batch) => {
      return {
        id: e.id,
        name: e.name,
        course_code: e.course_code,
        category: e.category?.name,
        enrolled_students: e.enrolled_students,
        active_students: e.active_students,
      };
    }),
    filename: "batch-list",
  });
};

export const exportBatchWiseStudents = (data: Student[], batch: Batch) => {
  const rows = data.map((e) => {
    const enrollment = e.batches?.find((b) => b.batch_id === batch.id);
    const active = enrollment ? (enrollment.active ? "Active" : "Inactive") : "NA";
    return studentExportColumns(e, {
      Status: active,
      "Enrolled On": formatIstDateTime(enrollment?.created_at),
    });
  });

  if (!rows.length) return;

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  const headers = Object.keys(rows[0]);
  autoFitExcelColumns(ws, rows, headers);
  styleExcelHeaderRow(ws, headers.length);
  XLSX.utils.book_append_sheet(wb, ws, "Students");
  XLSX.writeFile(wb, `student-${batch?.course_code}.xlsx`);
};

export const exportFulfilmentPayments = (data: any[], status: string) => {
  return exportCsv({
    header: {
      buyer_name: "Student Name",
      buyer_email: "Student Email",
      registration_number: "Registration Number",
      sku_code: "SKU",
      sku_name: "Product",
      payment_date: "Payment Date (IST)",
      batch: "Batch",
      delivery: "Delivery Snapshot",
      dispatch_date: "Dispatch Date (IST)",
      delivered_at: "Delivered At (IST)",
      fulfilment: "Fulfilment",
      postal_fee_created: "Postal Fee Created",
    },
    data: data.map((e) => {
      const paymentDateRaw =
        e.cart_order?.payment_date ?? e.payment?.payment_date;
      const paymentDate =
        paymentDateRaw && moment(paymentDateRaw).isValid()
          ? `${formatDateTimeIst(paymentDateRaw, "DD MMMM, YYYY hh:mm A")} IST`
          : "?";
      const dispatchDate =
        e.dispatch_date && moment(e.dispatch_date).isValid()
          ? `${formatDateTimeIst(e.dispatch_date)} IST`
          : "";
      const deliveredAt =
        e.shipment?.delivered_at && moment(e.shipment.delivered_at).isValid()
          ? `${formatDateTimeIst(e.shipment.delivered_at)} IST`
          : "";
      const delivery = [
        e.delivery_address_line1,
        e.delivery_city,
        e.delivery_state,
        e.delivery_pincode,
        e.delivery_phone,
      ]
        .filter(Boolean)
        .join(", ");
      return {
        buyer_name:
          get(e, "cart_order.student.name") ?? get(e, "payment.student.name"),
        buyer_email:
          get(e, "cart_order.student.email") ?? get(e, "payment.student.email"),
        registration_number:
          get(e, "cart_order.student.registration_number") ??
          get(e, "payment.student.registration_number"),
        sku_code: e.inventory?.sku_code ?? "",
        sku_name: e.inventory?.name ?? "",
        payment_date: paymentDate,
        batch:
          e.batch?.name ||
          e.batch?.course_code ||
          e.payment?.batch?.course_code ||
          "-",
        delivery: delivery || "-",
        dispatch_date: dispatchDate || "-",
        delivered_at: deliveredAt || "-",
        fulfilment: e.status ?? status,
        postal_fee_created: e.is_postal_fee_created ? "Yes" : "No",
      };
    }),
    filename: `fulfilments-${status}-${new Date().toLocaleDateString()}_${new Date().getTime()}`,
  });
};

export const exportFulfilmentSummery = (data: FulfilmentList[]) => {
  return exportCsv({
    header: {
      student_name: "Student",
      student_email: "Email",
      registration_number: "Reg. no.",
      receipt_no: "Receipt",
      batch_codes: "Batch Codes",
      sku_codes: "SKUs",
      awb_number: "AWB / Tracking",
      carrier: "Carrier",
      postal_fee_created: "Postal Fee Created",
      fulfilment_stage: "Fulfilment stage",
      shipment_status: "Shipment status",
    },
    data: data.map((e: FulfilmentList) => {
      return {
        id: e.id,
        student_name: e.student_name ?? e.inventory_name ?? "—",
        student_email: e.student_email ?? "",
        registration_number: e.registration_number ?? "",
        receipt_no: e.receipt_no ?? "",
        batch_codes: e.batch_codes ?? "",
        sku_codes: e.sku_codes ?? e.sku_code ?? "",
        awb_number: e.awb_number ?? "",
        carrier: /^manual$/i.test(e.carrier || "")
          ? "Manual"
          : e.delivery_partner || e.carrier || "",
        postal_fee_created: e.is_postal_fee_created ? "Yes" : "No",
        fulfilment_stage: e.fulfilment_stage ?? "",
        shipment_status: e.shipment_status ?? "",
      };
    }),
    filename: `fulfilments-summery-${new Date().toLocaleDateString()}_${new Date().getTime()}`,
  });
};

export const exportPayments = (data: Payment[]) => {
  return exportCsv({
    data,
    filename: `payment-report-${new Date().toLocaleDateString()}_${new Date().getTime()}`,
  });
};

export const exportStudents = (data: Student[]) => {
  const rows = data.map((e) =>
    studentExportColumns(e, { Status: e.active ? "Active" : "Inactive" }),
  );

  if (!rows.length) return;

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  const headers = Object.keys(rows[0]);
  autoFitExcelColumns(ws, rows, headers);
  styleExcelHeaderRow(ws, headers.length);
  XLSX.utils.book_append_sheet(wb, ws, "Students");
  XLSX.writeFile(
    wb,
    `student-report-${new Date().toLocaleDateString()}-${new Date().getTime()}.xlsx`,
  );
};

export const exportAttendance = (data: any[], batch: Batch) => {
  return exportCsv({
    data: data.map(({ name, registration_number, ...e }) => ({
      Name: name,
      "Reg No.": registration_number,
      ...e,
    })),
    filename: `batch-attendance-report-${batch.course_code}-${new Date().toLocaleDateString()}-${new Date().getTime()}`,
  });
};

export const exportPerformance = (data: string, batch: Batch) => {
  const blob = new Blob([data], { type: "text/csv" });
  const a = document.createElement("a");
  const url = window.URL.createObjectURL(blob);

  a.href = url;
  a.download = `batch-perforomance-report-${
    batch.course_code
  }-${new Date().toLocaleDateString()}-${new Date().getTime()}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
  // return exportCsv({
  //   data,
  //   filename: `batch-perforomance-report-${
  //     batch.course_code
  //   }-${new Date().toLocaleDateString()}-${new Date().getTime()}`,
  // });
};
